import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { api } from "../lib/api";
import { useMarket } from "../lib/store";
import { useWatchlists } from "../lib/watchlists";
import { ExTag } from "./Price";

const GROUP = { EQ: "Equity", INDEX: "Indices", FUT: "Futures", CE: "Options", PE: "Options" };

export function SearchDialog() {
  const { searchOpen, setSearch, openOrder, addMeta } = useMarket();
  const add = useWatchlists((s) => s.add);
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);

  useEffect(() => {
    if (q.trim().length < 1) { setRes([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/instruments/search", { params: { q } });
        setRes(data);
        addMeta(Object.fromEntries(data.map((d) => [d.instrument_key, d])));
      } catch (e) { setRes([]); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, addMeta]);

  const groups = {};
  res.forEach((r) => { const g = GROUP[r.instrument_type] || "Equity"; (groups[g] = groups[g] || []).push(r); });
  const close = () => { setSearch(false); setQ(""); };

  return (
    <CommandDialog open={searchOpen} onOpenChange={(o) => (o ? setSearch(true) : close())} shouldFilter={false}>
      <CommandInput data-testid="global-search-input" value={q} onValueChange={setQ}
        placeholder="Search stocks, indices, futures, options… (e.g. NIFTY 25000 CE)" />
      <CommandList className="max-h-[420px] thin-scroll">
        <CommandEmpty>{q ? "No instruments found." : "Start typing to search NSE, BSE and MCX."}</CommandEmpty>
        {Object.entries(groups).map(([g, rows]) => (
          <CommandGroup key={g} heading={g}>
            {rows.map((r) => (
              <CommandItem key={r.instrument_key} value={r.instrument_key} data-testid={`search-result-${r.trading_symbol}`}
                onSelect={() => { close(); nav(`/instrument/${encodeURIComponent(r.instrument_key)}`); }}
                className="group flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium truncate">{r.trading_symbol}</div>
                  <div className="text-[10.5px] text-muted-foreground truncate">{r.name}{r.lot_size > 1 ? ` · Lot ${r.lot_size}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <ExTag ex={r.exchange} />
                  {r.instrument_type !== "INDEX" && (<>
                    <span role="button" data-testid={`search-buy-${r.trading_symbol}`} onClick={(e) => { e.stopPropagation(); close(); openOrder({ key: r.instrument_key, side: "BUY" }); }}
                      className="h-5 px-1.5 rounded bg-up text-white text-[10px] font-bold">B</span>
                    <span role="button" data-testid={`search-sell-${r.trading_symbol}`} onClick={(e) => { e.stopPropagation(); close(); openOrder({ key: r.instrument_key, side: "SELL" }); }}
                      className="h-5 px-1.5 rounded bg-down text-white text-[10px] font-bold">S</span>
                  </>)}
                  <span role="button" data-testid={`search-add-${r.trading_symbol}`} onClick={(e) => { e.stopPropagation(); add(r.instrument_key); }}
                    className="h-5 px-1.5 rounded border border-border text-[10px] font-bold">+</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="flex gap-4 px-3 py-2 border-t border-border text-[10px] text-muted-foreground">
        <span>↑↓ navigate</span><span>↵ open chart</span><span>Esc close</span>
      </div>
    </CommandDialog>
  );
}
